// ─── resolveClientMirror.js ─────────────────────────────────────────────
// Refonte écran Suivi — Phase 3 (socle). Voir docs/PLAN-REFONTE-ECRAN-SUIVI.md.
//
// Fonction PURE de la « bande miroir » : répond à « qu'est-ce que la cliente
// voit en ce moment, et qu'est-ce qui va lui arriver ». Consomme des données
// déjà récupérées (journey_state, statut clients-status, brouillon en attente)
// et renvoie un descripteur d'affichage. Ne fetch rien, ne modifie rien.
//
// Sources repliées :
//   - client.journey_state.current_step → état visible cliente (miroir de
//     SAAS_TO_CLIENT_APP dans journeyState.js)
//   - statusEntry (fetchClientsStatus) → publication programmée (via
//     describeRepublication)
//   - pendingDraft (plan_drafts_pending_review) → version en attente / expirant

import { describeRepublication } from './republicationNotice';

// Miroir de SAAS_TO_CLIENT_APP (journeyState.js) : étape SaaS → état vu par la
// cliente. Dupliqué ici volontairement (fonction pure sans dépendance au
// module de sync) ; à garder aligné si la carte de journeyState évolue.
const STEP_TO_CLIENT_STATE = Object.freeze({
  anamnesis: 'questionnaire',
  analyses: 'analyses',
  waiting_results: 'analyses',
  results: 'analyses',
  plan_generation: 'program_in_progress',
  plan_editing: 'program_in_progress',
  // La livraison est la porte de verification : le programme n'est actif
  // qu'apres le succes reel de publish-plan, juste avant followup.
  delivery: 'program_in_progress',
  followup: 'program_active',
});

// Libellés côté praticienne (« ce que Melissa voit »).
const STATE_LABELS = Object.freeze({
  questionnaire: 'Remplit son questionnaire',
  analyses: 'Sur ses analyses',
  program_in_progress: 'Programme en préparation',
  program_active: 'Programme actif',
  welcome: 'Accueil',
});

/** Nombre de jours entiers entre deux dates (Date ou ISO). Null si invalide. */
function diffDays(from, to) {
  const a = from instanceof Date ? from.getTime() : Date.parse(from);
  const b = to instanceof Date ? to.getTime() : Date.parse(to);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.floor((b - a) / 86400000);
}

/**
 * Construit le descripteur de la bande miroir.
 *
 * @param {object|null} client - ligne `clients` (lit client.journey_state)
 * @param {object} [opts]
 * @param {object|null} [opts.statusEntry] - entrée clients-status (fetchClientsStatus)
 * @param {object|null} [opts.pendingDraft] - brouillon en attente { created_at, expires_at? }
 * @param {string|null} [opts.activePhaseName] - nom de la phase active (si connu)
 * @param {Date} [opts.now]
 * @returns {{
 *   visible: { state: string, label: string, isLive: boolean },
 *   activePhase: string | null,
 *   alert: null | {
 *     kind: 'pending' | 'scheduled' | 'verification_impossible',
 *     sinceDays?: number|null,
 *     expiresInDays?: number|null,
 *     dateISO?: string,
 *   },
 * }}
 *
 * @param {boolean} [opts.draftsCheckFailed] - la recherche de brouillon en
 *   attente n'a pas abouti. Revue ergo 2026-08-21 : cet échec partait dans un
 *   catch muet, `pendingDraft` restait null, et l'absence d'alerte se lisait
 *   comme « rien en attente ». Un brouillon qui expire pouvait donc passer
 *   inaperçu. On ne dit plus « tout va bien » quand on n'a pas pu regarder.
 */
export function resolveClientMirror(client, opts = {}) {
  const {
    statusEntry = null,
    pendingDraft = null,
    activePhaseName = null,
    draftsCheckFailed = false,
    now = new Date(),
  } = opts;

  const step = client?.journey_state?.current_step || null;
  const state = STEP_TO_CLIENT_STATE[step] || 'welcome';
  const label = STATE_LABELS[state] || STATE_LABELS.welcome;
  const isLive = state === 'program_active';

  // Alerte, par ordre d'urgence : brouillon en attente de validation (action
  // d'Anissa requise, peut expirer) PRIME sur une publication simplement
  // programmée.
  let alert = null;
  if (pendingDraft && pendingDraft.created_at) {
    alert = {
      kind: 'pending',
      sinceDays: diffDays(pendingDraft.created_at, now),
      expiresInDays: pendingDraft.expires_at ? diffDays(now, pendingDraft.expires_at) : null,
    };
  } else if (draftsCheckFailed) {
    // Un brouillon connu prime : on ne masque jamais une alerte réelle par un
    // aveu d'ignorance. Mais faute d'avoir pu regarder, on le dit.
    alert = { kind: 'verification_impossible' };
  } else {
    const repub = describeRepublication(statusEntry, now);
    if (repub && repub.variant === 'scheduled') {
      alert = { kind: 'scheduled', dateISO: repub.dateISO };
    }
  }

  return {
    visible: { state, label, isLive },
    activePhase: activePhaseName || null,
    alert,
  };
}
